import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  LinearProgress,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { OverviewAPI } from 'api';
import { listClients } from 'api/clientsApi';
import { ACTIVE_JOB_STATUSES, JobTableType, StatusValue } from 'api/jobApi';
import { buildJobListUrl } from '../jobListUrlParams';
import useIsMobile from 'theme/useIsMobile';
import StatusFilterSelect, { allStatus } from './StatusFilter';
import DateRangeFilter from './DateRangeFilter';
import {
  cardSx,
  chartTokens,
  columnHeaderSx,
  footerSx,
  formatCount,
  subtitleSx,
  tabularNumSx,
  titleSx,
} from './dashboardChartTokens';

/** Clients listed before the "See all clients" link is used. */
const COLLAPSED_ROW_COUNT = 10;

interface Props {
  label?: string;
  /** Narrows the counts to jobs where this user is the PM or supervisor. */
  coordinatorId?: string;
}

interface ClientRow {
  clientId: string;
  companyName: string;
  legalName: string;
  total: number;
  counts: Record<StatusValue, number>;
}

const StatusBar = ({
  row,
  segments,
  scaleMax,
  onSegmentClick,
}: {
  row: ClientRow;
  segments: { label: StatusValue; color: string }[];
  scaleMax: number;
  onSegmentClick?: (status: StatusValue) => void;
}) => {
  const widthOf = (count: number) => (scaleMax > 0 ? (count / scaleMax) * 100 : 0);

  return (
    <Box
      sx={{
        position: 'relative',
        height: 26,
        borderRadius: '6px',
        backgroundColor: chartTokens.track,
        overflow: 'hidden',
        display: 'flex',
      }}>
      {segments.map((segment, index) => {
        const count = row.counts[segment.label] ?? 0;
        if (count <= 0) return null;
        const width = widthOf(count);
        return (
          <Tooltip
            key={segment.label}
            title={`${segment.label}: ${formatCount(count)}${
              onSegmentClick ? ' — click to view jobs' : ''
            }`}>
            <Box
              onClick={
                onSegmentClick
                  ? (event) => {
                      // Keep the row-level drill-through from also firing.
                      event.stopPropagation();
                      onSegmentClick(segment.label);
                    }
                  : undefined
              }
              sx={{
                height: '100%',
                width: `${width}%`,
                minWidth: count > 0 ? 3 : 0,
                backgroundColor: segment.color,
                // Round only the ends of the bar, not the internal joins.
                borderTopLeftRadius: index === 0 ? '6px' : 0,
                borderBottomLeftRadius: index === 0 ? '6px' : 0,
                borderTopRightRadius: index === segments.length - 1 ? '6px' : 0,
                borderBottomRightRadius: index === segments.length - 1 ? '6px' : 0,
                transition: 'width 300ms ease',
                cursor: onSegmentClick ? 'pointer' : 'default',
                '&:hover': onSegmentClick ? { opacity: 0.8 } : undefined,
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
};

const JobStatusByClientChart = ({ label, coordinatorId }: Props) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile('xs');

  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [rangeSummary, setRangeSummary] = useState('all time');

  const [showAll, setShowAll] = useState(false);

  // The statuses that make up an open workload are selected by default.
  const [selectedStatus, setSelectedStatus] =
    useState<StatusValue[]>(ACTIVE_JOB_STATUSES);

  const { isFetching, data } = useQuery(
    ['client-job-status', startDate, endDate, coordinatorId],
    () => OverviewAPI.fetchJobStatusByClient(startDate, endDate, coordinatorId)
  );

  // The job list's client filter matches legal names, while this endpoint
  // returns trading names — resolve each row's client id to its legal name so
  // the drill-through lands on the same client the row counts.
  const { data: clients } = useQuery(['list-clients'], listClients);

  const segments = useMemo(
    () => allStatus.filter((status) => selectedStatus.includes(status.label)),
    [selectedStatus]
  );

  /**
   * Drill-throughs. Row -> that client's jobs in the selected statuses; bar
   * segment -> additionally narrowed to the one status clicked.
   */
  const drillToClientJobs = (clientId: string, companyName: string) => {
    const legalName =
      clients?.find((client) => client.id === clientId)?.legalName ??
      companyName;
    navigate(
      buildJobListUrl({
        tab: JobTableType.ALL,
        client: [legalName],
        status: selectedStatus.length ? selectedStatus : undefined,
        coordinator: coordinatorId ? [coordinatorId] : undefined,
      })
    );
  };

  const drillToClientStatusJobs = (
    clientId: string,
    companyName: string,
    status: StatusValue
  ) => {
    const legalName =
      clients?.find((client) => client.id === clientId)?.legalName ??
      companyName;
    navigate(
      buildJobListUrl({
        tab: JobTableType.ALL,
        client: [legalName],
        status: [status],
        coordinator: coordinatorId ? [coordinatorId] : undefined,
      })
    );
  };

  const rows: ClientRow[] = useMemo(() => {
    const statuses = selectedStatus.length
      ? selectedStatus
      : allStatus.map((ob) => ob.label);

    return (data ?? [])
      .map((client) => ({
        clientId: client.clientId,
        companyName: client.companyName,
        legalName:
          clients?.find((c) => c.id === client.clientId)?.legalName ??
          client.companyName,
        total: statuses.reduce(
          (total, status) => total + (Number(client[status]) || 0),
          0
        ),
        counts: Object.fromEntries(
          allStatus.map((ob) => [ob.label, Number(client[ob.label]) || 0])
        ) as Record<StatusValue, number>,
      }))
      .filter((row) => row.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [data, selectedStatus, clients]);

  const visibleRows = showAll ? rows : rows.slice(0, COLLAPSED_ROW_COUNT);
  const totalJobs = visibleRows.reduce((total, row) => total + row.total, 0);
  const scaleMax = Math.max(...rows.map((row) => row.total), 1);

  // Naming every status makes for an unreadable subtitle once a few are on.
  const statusSummary = !selectedStatus.length
    ? 'All statuses'
    : selectedStatus.length === ACTIVE_JOB_STATUSES.length &&
      ACTIVE_JOB_STATUSES.every((status) => selectedStatus.includes(status))
    ? 'Open jobs'
    : selectedStatus.length <= 2
    ? `${selectedStatus.join(' and ')} jobs`
    : `Jobs across ${selectedStatus.length} statuses`;

  return (
    <Card sx={cardSx}>
      {Boolean(isFetching) && <LinearProgress />}
      <CardContent sx={{ padding: { xs: 2.5, md: 3 }, paddingBottom: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}>
          <Box>
            <Typography sx={titleSx}>{label ?? 'Job Status by Client'}</Typography>
            <Typography sx={subtitleSx}>
              {`${statusSummary}, highest first · ${formatCount(
                totalJobs
              )} jobs shown · ${rangeSummary}`}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              alignItems: 'center',
            }}>
            <StatusFilterSelect
              value={selectedStatus}
              onChange={setSelectedStatus}
              width={520}
            />

            <DateRangeFilter
              onChange={(start, end, summary) => {
                setStartDate(start);
                setEndDate(end);
                setRangeSummary(summary);
              }}
            />
          </Box>
        </Box>
      </CardContent>

      <Divider sx={{ borderColor: chartTokens.divider, marginTop: 2 }} />

      <CardContent
        sx={{
          flex: 1,
          padding: { xs: 2.5, md: 3 },
          paddingTop: { xs: 2, md: 2 },
        }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isMobile
              ? 'minmax(0, 1fr) 48px'
              : 'minmax(0, 220px) minmax(0, 1fr) 56px',
            alignItems: 'center',
            columnGap: 2,
            paddingBottom: 1.5,
          }}>
          <Typography sx={columnHeaderSx}>Client</Typography>
          {!isMobile && <Typography sx={columnHeaderSx}>Status</Typography>}
          <Typography sx={{ ...columnHeaderSx, textAlign: 'right' }}>
            Jobs
          </Typography>
        </Box>
        <Divider sx={{ borderColor: chartTokens.divider }} />

        {isFetching && !rows.length
          ? Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} height={52} />
            ))
          : visibleRows.map((row) => (
              <Box
                key={row.clientId}
                role="button"
                tabIndex={0}
                onClick={() => drillToClientJobs(row.clientId, row.companyName)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    drillToClientJobs(row.clientId, row.companyName);
                  }
                }}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: isMobile
                    ? 'minmax(0, 1fr) 48px'
                    : 'minmax(0, 220px) minmax(0, 1fr) 56px',
                  alignItems: 'center',
                  columnGap: 2,
                  paddingY: 1.25,
                  borderBottom: `1px solid ${chartTokens.divider}`,
                  cursor: 'pointer',
                  transition: 'background-color 150ms ease',
                  '&:hover': { backgroundColor: chartTokens.accentMuted },
                }}>
                <Tooltip title={row.companyName}>
                  <Typography
                    noWrap
                    sx={{
                      fontSize: 14.5,
                      fontWeight: 500,
                      color: chartTokens.heading,
                    }}>
                    {row.companyName}
                  </Typography>
                </Tooltip>

                {!isMobile && (
                  <StatusBar
                    row={row}
                    segments={segments}
                    scaleMax={scaleMax}
                    onSegmentClick={(status) =>
                      drillToClientStatusJobs(
                        row.clientId,
                        row.companyName,
                        status
                      )
                    }
                  />
                )}

                <Typography
                  sx={{
                    fontSize: 15,
                    fontWeight: 700,
                    textAlign: 'right',
                    color: chartTokens.heading,
                    ...tabularNumSx,
                  }}>
                  {formatCount(row.total)}
                </Typography>
              </Box>
            ))}

        {!isFetching && !rows.length && (
          <Typography sx={{ ...footerSx, paddingY: 3, textAlign: 'center' }}>
            No clients to show for this selection.
          </Typography>
        )}

        {Boolean(rows.length) && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingTop: 2,
            }}>
            <Typography sx={footerSx}>
              {`Showing ${visibleRows.length} of ${rows.length} client${
                rows.length === 1 ? '' : 's'
              } · bars are relative to the busiest client`}
            </Typography>
            {rows.length > COLLAPSED_ROW_COUNT && (
              <Button
                disableRipple
                endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                onClick={() => setShowAll((current) => !current)}
                sx={{
                  textTransform: 'none',
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: chartTokens.accent,
                  padding: 0,
                  minWidth: 0,
                  '&:hover': { backgroundColor: 'transparent' },
                }}>
                {showAll ? 'Show top 10' : 'See all clients'}
              </Button>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default JobStatusByClientChart;
